import { t } from '../i18n';
import {
  type MissionConfig,
  type ButtonType,
  MISSION_DEFAULTS,
  createDefaultLiftConfig,
  LiftConfig,
  VisitorDistribution,
} from '../types';
import '../styles/missionSettings.css';

/**
 * Hash a string into a number (32-bit integer)
 * Using a simple but effective hash function
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Callback when mission starts
 */
type OnStartMission = (config: MissionConfig) => void;

/**
 * Create and render the Mission Settings UI
 */
export function createMissionSettingsUI(
  container: HTMLElement,
  onStartMission: OnStartMission
): void {
  // State
  let numberOfFloors: number = MISSION_DEFAULTS.numberOfFloors;
  let numberOfLifts: number = MISSION_DEFAULTS.numberOfLifts;
  let seedKeywords: string = 'hello world';
  let maxVisitors: number = MISSION_DEFAULTS.defaultMaxVisitors;
  let visitorDistribution: VisitorDistribution = MISSION_DEFAULTS.defaultVisitorDistribution;
  let floorButtonTypes: ButtonType[] = Array(numberOfFloors).fill(MISSION_DEFAULTS.defaultButtonType);
  let liftConfigs: Array<LiftConfig> =
    Array(numberOfLifts).fill(null).map(() => ({
      weightLimit: MISSION_DEFAULTS.defaultWeightLimit,
      bottomFloor: 0,
      topFloor: numberOfFloors - 1,
      startingFloor: 0,
      liftMoveSpeedSeconds: MISSION_DEFAULTS.defaultLiftMoveSpeedSeconds,
    }));

  /**
   * Update floor configs when floor count changes
   */
  function updateFloorCount(newCount: number): void {
    numberOfFloors = Math.max(2, newCount);

    // Resize floor button types array
    while (floorButtonTypes.length < numberOfFloors) {
      floorButtonTypes.push(MISSION_DEFAULTS.defaultButtonType);
    }
    floorButtonTypes = floorButtonTypes.slice(0, numberOfFloors);

    // Update lift floors if needed
    liftConfigs = liftConfigs.map(lift => {
      // 1. Clamp floors to the actual building size
      const maxPossible = numberOfFloors - 1;
      let top = Math.min(lift.topFloor, maxPossible);
      let bottom = Math.max(0, Math.min(lift.bottomFloor, maxPossible));

      // 2. Ensure Top is actually higher than or equal to Bottom
      if (bottom > top) {
        [bottom, top] = [top, bottom]; // Swap them if they are inverted
      }

      // 3. Force startingFloor to be inside that range
      const start = Math.max(bottom, Math.min(top, lift.startingFloor));

      return {
        ...lift,
        topFloor: top,
        bottomFloor: bottom,
        startingFloor: start
      };
    });

    render();
  }

  /**
   * Update lift configs when lift count changes
   */
  function updateLiftCount(newCount: number): void {
    numberOfLifts = Math.max(1, newCount);

    while (liftConfigs.length < numberOfLifts) {
      liftConfigs.push({
        weightLimit: MISSION_DEFAULTS.defaultWeightLimit,
        startingFloor: 0,
        bottomFloor: 0,
        topFloor: numberOfFloors - 1,
        liftMoveSpeedSeconds: MISSION_DEFAULTS.defaultLiftMoveSpeedSeconds,
      });
    }
    liftConfigs = liftConfigs.slice(0, numberOfLifts);

    render();
  }

  /**
   * Build mission config from current state
   */
  function buildMissionConfig(): MissionConfig {
    const floors = floorButtonTypes.map(buttonType => ({ buttonType }));
    const lifts: Record<string, ReturnType<typeof createDefaultLiftConfig>> = {};

    liftConfigs.forEach((config, index) => {
      lifts[`lift${index}`] = {
        weightLimit: config.weightLimit,
        bottomFloor: config.bottomFloor,
        topFloor: config.topFloor,
        startingFloor: config.startingFloor,
        liftMoveSpeedSeconds: config.liftMoveSpeedSeconds,
      };
    });

    return {
      floors,
      lifts,
      seedId: hashString(seedKeywords),
      maxVisitors,
      visitorDistribution,
    };
  }

  /**
   * Render floor configuration items
   */
  function renderFloorConfigs(): string {
    return floorButtonTypes.map((buttonType, index) => `
      <div class="config-item">
        <div class="config-item-header">${index === 0 ? t('lobby') : `${t('floor')} ${index}`}</div>
        <div class="config-item-row">
          <span class="config-item-label">Button Type</span>
          <select class="setting-select config-item-input" data-floor-index="${index}">
            <option value="SingleCall" ${buttonType === 'SingleCall' ? 'selected' : ''}>${t('buttonTypeSingleCall')}</option>
            <option value="UpDown" ${buttonType === 'UpDown' ? 'selected' : ''}>${t('buttonTypeUpDown')}</option>
            <option value="NumberedFloors" ${buttonType === 'NumberedFloors' ? 'selected' : ''}>${t('buttonTypeNumberedFloors')}</option>
          </select>
        </div>
      </div>
    `).join('');
  }

  /**
   * Render lift configuration items
   */
  function renderLiftConfigs(): string {
    return liftConfigs.map((config, index) => `
      <div class="config-item">
        <div class="config-item-header">Lift ${index + 1}</div>
        <div class="config-item-row">
          <span class="config-item-label">${t('weightLimit')}</span>
          <input type="number" class="setting-input config-item-input" 
                 data-lift-index="${index}" data-lift-field="weightLimit"
                 value="${config.weightLimit}" min="1">
        </div>
        <div class="config-item-row">
          <span class="config-item-label">${t('startingFloor')}</span>
          <input type="number" class="setting-input config-item-input" 
                 data-lift-index="${index}" data-lift-field="startingFloor"
                 value="${config.startingFloor}" min="0" max="${numberOfFloors - 1}">
        </div>
        <div class="config-item-row">
          <span class="config-item-label">${t('bottomFloor')}</span>
          <input type="number" class="setting-input config-item-input" 
                 data-lift-index="${index}" data-lift-field="bottomFloor"
                 value="${config.bottomFloor}" min="0" max="${numberOfFloors - 1}">
        </div>
        <div class="config-item-row">
          <span class="config-item-label">${t('topFloor')}</span>
          <input type="number" class="setting-input config-item-input" 
                 data-lift-index="${index}" data-lift-field="topFloor"
                 value="${config.topFloor}" min="0" max="${numberOfFloors - 1}">
        </div>
        <div class="config-item-row">
          <span class="config-item-label">${t('liftMoveSpeedSeconds')}</span>
          <input type="number" class="setting-input config-item-input" 
                 data-lift-index="${index}" data-lift-field="liftMoveSpeedSeconds"
                 value="${config.liftMoveSpeedSeconds}" min="0.1" max="10" step="0.1">
        </div>
      </div>
    `).join('');
  }

  /**
   * Render the full settings UI
   */
  function render(): void {
    container.innerHTML = `
      <div class="mission-settings-overlay">
        <div class="mission-settings">
          <h1>${t('missionSettings')}</h1>

          <div class="settings-section">
            <div class="setting-row">
              <span class="setting-label">${t('numberOfFloors')}</span>
              <input type="number" class="setting-input" id="floor-count" 
                     value="${numberOfFloors}" min="2" max="10">
            </div>
            <div class="setting-row">
              <span class="setting-label">${t('numberOfLifts')}</span>
              <input type="number" class="setting-input" id="lift-count" 
                     value="${numberOfLifts}" min="1" max="5">
            </div>
            <div class="setting-row">
              <span class="setting-label">Seed Keywords</span>
              <input type="text" class="setting-input keywords" id="seed-keywords" 
                     value="${seedKeywords}" placeholder="e.g. sunny morning">
            </div>
            <div class="setting-row">
              <span class="setting-label">Max Visitors</span>
              <input type="number" class="setting-input" id="max-visitors" 
                     value="${maxVisitors}" min="0">
            </div>
            <div class="setting-row">
              <span class="setting-label">Distribution</span>
              <select class="setting-select" id="visitor-distribution">
                <option value="Random" ${visitorDistribution === 'Random' ? 'selected' : ''}>Random</option>
                <option value="Interval" ${visitorDistribution === 'Interval' ? 'selected' : ''}>Interval</option>
                <option value="Burst" ${visitorDistribution === 'Burst' ? 'selected' : ''}>Burst</option>
                <option value="Sine" ${visitorDistribution === 'Sine' ? 'selected' : ''}>Sine</option>
              </select>
            </div>
          </div>

          <h2>Floor Configuration</h2>
          <div class="config-list">
            ${renderFloorConfigs()}
          </div>

          <h2>Lift Configuration</h2>
          <div class="config-list">
            ${renderLiftConfigs()}
          </div>

          <button class="start-mission-btn" id="start-mission">
            ${t('startMission')}
          </button>
        </div>
      </div>
    `;

    attachEventListeners();
  }

  /**
   * Attach event listeners to inputs
   */
  function attachEventListeners(): void {
    // Floor count
    const floorCountInput = container.querySelector('#floor-count') as HTMLInputElement;
    floorCountInput?.addEventListener('change', (e) => {
      updateFloorCount(parseInt((e.target as HTMLInputElement).value, 10));
    });

    // Lift count
    const liftCountInput = container.querySelector('#lift-count') as HTMLInputElement;
    liftCountInput?.addEventListener('change', (e) => {
      updateLiftCount(parseInt((e.target as HTMLInputElement).value, 10));
    });

    // Floor button types
    container.querySelectorAll('[data-floor-index]').forEach((select) => {
      select.addEventListener('change', (e) => {
        const index = parseInt((e.target as HTMLElement).dataset.floorIndex ?? '0', 10);
        floorButtonTypes[index] = (e.target as HTMLSelectElement).value as ButtonType;
      });
    });

    // Lift configs
    container.querySelectorAll('[data-lift-index]').forEach((input) => {
      input.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        const index = parseInt(target.dataset.liftIndex ?? '0', 10);
        const field = target.dataset.liftField as 'weightLimit' | 'startingFloor' | 'bottomFloor' | 'topFloor' | 'liftMoveSpeedSeconds';
        const config = liftConfigs[index];
        if (config && field) {
          config[field] = parseInt(target.value, 10);
        }
      });
    });

    // Start button
    const startBtn = container.querySelector('#start-mission');
    startBtn?.addEventListener('click', () => {
      const config = buildMissionConfig();
      onStartMission(config);
    });

    // Seed Keywords
    const seedInput = container.querySelector('#seed-keywords') as HTMLInputElement;
    seedInput?.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const filtered = target.value.toLowerCase().replace(/[^a-z\s]/g, '');

      // Update the input field if the value changed after filtering
      if (target.value !== filtered) {
        target.value = filtered;
      }

      seedKeywords = filtered;
      if (!seedKeywords) seedKeywords = 'hello world';
    });

    // Max Visitors
    const maxVisitorsInput = container.querySelector('#max-visitors') as HTMLInputElement;
    maxVisitorsInput?.addEventListener('change', (e) => {
      maxVisitors = parseInt((e.target as HTMLInputElement).value, 10);
      if (isNaN(maxVisitors)) maxVisitors = MISSION_DEFAULTS.defaultMaxVisitors;
    });

    // Visitor Distribution
    const distributionSelect = container.querySelector('#visitor-distribution') as HTMLSelectElement;
    distributionSelect?.addEventListener('change', (e) => {
      visitorDistribution = (e.target as HTMLSelectElement).value as VisitorDistribution;
    });
  }

  // Initial render
  render();
}

/**
 * Remove mission settings UI
 */
export function removeMissionSettingsUI(container: HTMLElement): void {
  container.innerHTML = '';
}
